/**
 * AccountSettings - Modern Account Settings Page Redesign
 * Design patterns: Notion, Arc, Linear
 * 
 * Features:
 * - Two-column layout with sticky sidebar navigation
 * - Modern profile header with avatar
 * - Unified appearance controls
 * - Visual layout preview cards
 * - Collapsible danger zone
 * - Toast notifications for feedback
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProtectedRoute from './ProtectedRoute.mjs';
import { setPageTitle, setPageDescription } from '../../slices/pageMetadataSlice.mjs';

// Import new settings section components
import {
    AccountSettingsSidebar,
    ProfileSection,
    AppearanceSection,
    LayoutSection,
    LiveZoneSection,
    ForYouSection,
    NotificationsSection,
    DangerZoneSection,
} from './settings/index.mjs';

/**
 * Toast notification component
 */
function SettingsToast({ message, onClose }) {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    if (!message) return null;

    return (
        <div className={`settings-toast ${message.type}`}>
            <span className="settings-toast-icon">
                {message.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="settings-toast-message">{message.text}</span>
        </div>
    );
}

/**
 * Main Account Settings Content
 */
function AccountSettingsContent() {
    const [activeSection, setActiveSection] = useState('profile');
    const [toast, setToast] = useState(null);
    const sectionRefs = useRef({});
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth || {});

    // Set page metadata
    useEffect(() => {
        dispatch(setPageTitle('Account Settings - Nexus of Truth'));
        dispatch(setPageDescription('Manage your account settings, appearance, and preferences.'));
    }, [dispatch]);

    // Handle smooth scroll navigation
    const handleNavigate = useCallback((sectionId) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    // Handle preference change feedback
    const handlePreferenceChange = useCallback((message) => {
        setToast(message);
    }, []);

    // Close toast
    const handleCloseToast = useCallback(() => {
        setToast(null);
    }, []);

    // Intersection observer for active section tracking
    useEffect(() => {
        const sections = ['profile', 'notifications', 'appearance', 'layouts', 'livezone', 'for-you-section', 'danger'];

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-100px 0px -50% 0px', threshold: 0 }
        );

        // Observe sections after a short delay to ensure they're mounted
        const timer = setTimeout(() => {
            sections.forEach((id) => {
                const element = document.getElementById(id);
                if (element) {
                    observer.observe(element);
                }
            });
        }, 100);

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, []);

    // Loading state
    if (!user) {
        return (
            <div className="account-settings-page">
                <main className="account-settings-main">
                    <div className="settings-content">
                        <div className="settings-section">
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <span style={{ fontSize: '32px', marginBottom: '16px', display: 'block' }}>⏳</span>
                                <p>Loading account information...</p>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="account-settings-page">
            <main className="account-settings-main">
                {/* Sidebar Navigation */}
                <AccountSettingsSidebar
                    activeSection={activeSection}
                    onNavigate={handleNavigate}
                />

                {/* Content Sections */}
                <div className="settings-content">
                    <ProfileSection user={user} />

                    <NotificationsSection />

                    <AppearanceSection
                        preferences={user.preferences}
                        onPreferenceChange={handlePreferenceChange}
                    />

                    <LayoutSection
                        preferences={user.preferences}
                        onPreferenceChange={handlePreferenceChange}
                    />

                    <LiveZoneSection
                        preferences={user.preferences}
                        onPreferenceChange={handlePreferenceChange}
                    />

                    <ForYouSection />

                    <DangerZoneSection />
                </div>
            </main>

            {/* Toast Notification */}
            <SettingsToast message={toast} onClose={handleCloseToast} />
        </div>
    );
}

/**
 * Main AccountSettings component with protected route wrapper
 */
const AccountSettings = (props) => (
    <ProtectedRoute>
        <AccountSettingsContent {...props} />
    </ProtectedRoute>
);

export default AccountSettings;
